/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/no-array-index-key */
import mdast from 'mdast';
import React, {FC, Fragment, ReactNode, useMemo} from 'react';

import {markdownAstFromString} from './crossPlatformImports/markdownAstFromString';
import {NodeTransform} from './markdownFormatterTypes';
import {MarkdownRendererErrorBoundary} from './markdownRendererErrorBoundary';

/*
 * Props.
 */

export interface MarkdownPlainRendererProps {
  debugIdentifier: string;
  text: string;
  ErrorFallbackComponent?: React.ComponentType<{error: unknown; debugIdentifier: string}>;
  transform?: NodeTransform;
  onLinkClick?: (link: string, shiftKey?: boolean) => void;
}

/*
 * Component.
 */

const ErrorFallbackComponentEmpty: FC = () => '';

export const MarkdownPlainRenderer = (props: MarkdownPlainRendererProps) => {
  const {debugIdentifier, ErrorFallbackComponent = ErrorFallbackComponentEmpty} = props;
  return (
    <MarkdownRendererErrorBoundary
      debugIdentifier={debugIdentifier}
      FallBackComponent={ErrorFallbackComponent}
    >
      <RendererContent {...props} />
    </MarkdownRendererErrorBoundary>
  );
};

const RendererContent = (props: MarkdownPlainRendererProps) => {
  const {text, transform} = props;

  const ast = useMemo(() => markdownAstFromString(text), [text]);
  return useMemo(() => renderPlainMarkdownAst(ast, transform), [ast, transform]);
};

/**
 * Renders a markdown AST to a simplified inline render with as much text content displayed as possible.
 * @param root The Markdown AST Root.
 * @param textTransform function that may transform each strings into more complex React nodes (mentions, inline unfurls, emojis...)
 */
function renderPlainMarkdownAst(root: mdast.Root, textTransform?: NodeTransform): ReactNode {
  let previousTextToken: string | undefined;

  const maybeApplyTransformText: NodeTransform = (node, previousToken) => {
    if (!textTransform) {
      return node;
    }
    return textTransform(node, previousToken);
  };

  const renderChildren = (children: mdast.RootContent[], listDepth: number, shouldTrim: boolean = true) => (
    <>
      {children.map((child, index) => (
        <Fragment key={index}>{render(child, listDepth)}</Fragment>
      ))}
    </>
  );

  const render = (node: mdast.Root | mdast.RootContent, listDepth: number): ReactNode => {
    switch (node.type) {
      case 'root':
      case 'paragraph':
      case 'blockquote':
      case 'emphasis':
      case 'strong':
      case 'delete':
      case 'link':
        return renderChildren(node.children, listDepth);

      case 'list': {
        // Only used on mobile, as web automatically computes list numbers
        return node.children.map((child, index) => {
          const key = `${listDepth}-${index}`;
          // Case 1 - the only content in the list item is the list, so we don't render the list item at all.
          if (child.children.length === 1 && child.children[0].type === 'list') {
            return <Fragment key={key}>{render(child.children[0], listDepth + 1)}</Fragment>;
          }
          // Case 2 - There is content in the list item before the nested list. So we first need to render
          // that content, and then the list.
          return <Fragment key={key}>{renderChildren(child.children, listDepth + 1)}</Fragment>;
        });
      }

      case 'code': {
        const shouldAddLeadingWhiteSpace = previousTextToken !== undefined;
        const trimmedText = optimizeWhitespace(node.value, shouldAddLeadingWhiteSpace);
        previousTextToken = trimmedText;
        return node.value;
      }

      case 'text':
      case 'inlineCode': {
        const shouldAddLeadingWhiteSpace = previousTextToken !== undefined;
        const trimmedText = optimizeWhitespace(node.value, shouldAddLeadingWhiteSpace);
        const transformedNode = maybeApplyTransformText(trimmedText, previousTextToken);
        previousTextToken = trimmedText;
        return transformedNode;
      }

      default:
        return null;
    }
  };

  return render(root, 0);
}

function optimizeWhitespace(text: string, shouldAddLeadingWhiteSpace: boolean): string {
  // check if text is just whitespace
  if (!text.trim()) {
    return '';
  }
  const textWithoutExtraSpaces = text.replace(/\s+/g, ' ');
  if (shouldAddLeadingWhiteSpace) {
    return ` ${textWithoutExtraSpaces.trim()}`;
  }
  return textWithoutExtraSpaces.trim();
}
